// src/features/WhatIfAnalysis/components/WhatIfAnalysisDashboard.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  PlayArrow,
  Add,
  TrendingUp,
  Savings,
  Schedule
} from '@mui/icons-material';
import { useI18n } from '../../../i18n/i18n';
import useWhatIfAnalysis from '../hooks/useWhatIfAnalysis';

// ВИПРАВЛЕНО: Створюємо тимчасові компоненти для відсутніх
const PredefinedScenarios = ({ scenarios, onRunScenario, isLoading, t }) => (
  <Grid container spacing={3}>
    {scenarios.map((scenario, index) => (
      <Grid item xs={12} md={6} key={index}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              {scenario.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {scenario.description}
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={() => onRunScenario(scenario.type)}
              disabled={isLoading}
              fullWidth
            >
              {t('whatIf.runScenario')}
            </Button>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const ScenarioResults = ({ scenarios, currentScenario, isLoading, t }) => (
  <Box>
    {currentScenario ? (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Результати аналізу: {currentScenario.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('whatIf.scenarioParameters')}: {currentScenario.parameters}
          </Typography>
        </CardContent>
      </Card>
    ) : (
      <Alert severity="info">
        {t('whatIf.noResults')} - {t('whatIf.runAnalysisFirst')}
      </Alert>
    )}
  </Box>
);

const ScenarioWizard = ({ open, onClose, onScenarioCreated, t }) => (
  open && (
    <Alert severity="info" sx={{ mt: 2 }}>
      Майстер створення сценаріїв буде реалізовано найближчим часом
      <Button onClick={onClose} sx={{ ml: 2 }} size="small">
        {t('whatIf.reset')}
      </Button>
    </Alert>
  )
);

const AnalysisStatus = ({ scenario, t }) => {
  if (!scenario) return null;

  const statusConfig = {
    created: { color: 'default', label: t('what_if_status_created') },
    running: { color: 'warning', label: t('what_if_status_running') },
    completed: { color: 'success', label: t('what_if_status_completed') },
    error: { color: 'error', label: t('what_if_status_error') }
  };

  const config = statusConfig[scenario.status] || statusConfig.created;

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="filled"
    />
  );
};

const WhatIfAnalysisDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { t } = useI18n();
  
  const {
    scenarios,
    currentScenario,
    isLoading,
    error,
    baseDataLoaded,
    runPredefinedScenario,
    getPredefinedScenarios
  } = useWhatIfAnalysis();

  const predefinedScenarios = getPredefinedScenarios();

  const handleRunPredefinedScenario = async (scenarioType) => {
    try {
      await runPredefinedScenario(scenarioType);
      setActiveTab(1); // Переходимо на вкладку результатів
    } catch (err) {
      console.error('Failed to run scenario:', err);
    }
  };

  if (!baseDataLoaded && isLoading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('what_if_loading_data')}
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {t('what_if_data_error')}: {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('whatIf.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('what_if_analysis_subtitle')}
        </Typography>
      </Box>

      {/* Вкладки */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('what_if_tab_predefined')} />
          <Tab label={t('what_if_tab_custom')} />
          <Tab label={t('what_if_tab_results')} />
        </Tabs>
      </Paper>

      {/* Вміст вкладок */}
      {activeTab === 0 && (
        <PredefinedScenarios
          scenarios={predefinedScenarios}
          onRunScenario={handleRunPredefinedScenario}
          isLoading={isLoading}
          t={t}
        />
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('what_if_custom_scenario')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('what_if_custom_scenario_desc')}
                </Typography>
                
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setWizardOpen(true)}
                  size="large"
                >
                  {t('what_if_create_custom')}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('what_if_quick_actions')}
                </Typography>
                
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<TrendingUp />}
                    onClick={() => handleRunPredefinedScenario('optimistic')}
                  >
                    {t('whatIf.scenarios.improvement_all')}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<Savings />}
                    onClick={() => handleRunPredefinedScenario('resource_saving')}
                  >
                    {t('whatIf.scenarios.minimal_changes')}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<TrendingUp />}
                    onClick={() => handleRunPredefinedScenario('high_capacity')}
                  >
                    {t('whatIf.scenarios.efficiency_focus')}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<Schedule />}
                    onClick={() => handleRunPredefinedScenario('max_production')}
                  >
                    {t('whatIf.scenarios.max_production')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <ScenarioResults
          scenarios={scenarios}
          currentScenario={currentScenario}
          isLoading={isLoading}
          t={t}
        />
      )}

      {/* Майстер створення сценаріїв */}
      <ScenarioWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onScenarioCreated={() => setActiveTab(2)}
        t={t}
      />

      {/* Статус аналізу */}
      {isLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {t('what_if_analyzing')}
          </Typography>
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}
    </Box>
  );
};

export default WhatIfAnalysisDashboard;